import { z } from 'zod';

import { DataProviderSchema } from './data_provider.schema';

/**
 * How the backend decided which provider a metric is read from.
 *
 * `mode` exists because a null `resolved` means three different things, and a
 * two-valued design would put a false "no provider selected" hint on every
 * per-day chart:
 *
 * - `single_provider` — one provider is elected for the whole window, so a null
 *   `resolved` really does mean nothing was elected.
 * - `per_day` — the winner is picked per day inside the aggregation, so
 *   `resolved` is null even on a completely full chart. Never derive a reason
 *   from it here.
 * - `not_applicable` — the metric has no provider concept (sport sessions/laps).
 *
 * @see docs/data_providers/CONFIGURATION.md
 */
export enum DATA_PROVIDER_RESOLUTION_MODES {
  SINGLE_PROVIDER = 'single_provider',
  PER_DAY = 'per_day',
  NOT_APPLICABLE = 'not_applicable',
}

export const DataProviderResolutionSchema = z
  .object({
    mode: z.nativeEnum(DATA_PROVIDER_RESOLUTION_MODES),
    /** The configured allowlist, in priority order. Null for un-migrated users. */
    ordered_providers: z.array(DataProviderSchema).nullable(),
    resolved: DataProviderSchema.nullable(),
    /**
     * Providers that DO have data in the window regardless of the allowlist.
     * Only populated when the series came back empty — it costs a query.
     */
    providers_with_data: z.array(DataProviderSchema),
  })
  .strict();

export type TDataProviderResolution = z.infer<typeof DataProviderResolutionSchema>;
